import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { api } from '../services/api';
import { Metrics } from '../types';
import { useConfigStore } from '../stores/useConfigStore';
import { getStageHexColor } from '../utils/stageColors';

/**
 * Overview Page
 * Dashboard home with key metrics
 */
export const OverviewPage: React.FC = () => {
  const { config } = useConfigStore();
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadMetrics();
  }, []);

  const loadMetrics = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getMetrics();
      setMetrics(data);
    } catch (err: any) {
      console.error('Failed to load metrics:', err);
      setError(err.message || 'Failed to load metrics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight={600}>
        Overview
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Customer health analytics at a glance
      </Typography>

      {/* Metrics Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
        {/* Total Accounts */}
        <Box sx={{ flex: '1 1 calc(25% - 18px)', minWidth: 200 }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Accounts
              </Typography>
              <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                <Typography variant="h3" fontWeight={600}>
                  {metrics?.totalAccounts || 0}
                </Typography>
                {!!metrics?.archivedAccounts && (
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {metrics.archivedAccounts} Archived
                    </Typography>
                    <Tooltip
                      title="Archived accounts have been churned for 60+ days. They are not billed and are hidden from the dashboard. Find them under Accounts → Stage → Archived."
                      arrow
                      placement="top"
                    >
                      <IconButton size="small" sx={{ p: 0 }}>
                        <InfoOutlined sx={{ fontSize: 14, color: 'text.secondary' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Active Accounts */}
        <Box sx={{ flex: '1 1 calc(25% - 18px)', minWidth: 200 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Recently Active
                </Typography>
                <Tooltip
                  title="Accounts with entity activity in the last 7 days, whatever their lifecycle stage. Not the same as the Active stage, which has its own rules."
                  arrow
                  placement="top"
                >
                  <IconButton size="small" sx={{ p: 0, mb: 0.5 }}>
                    <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="h3" fontWeight={600} color="success.main">
                {metrics?.activeAccounts || 0}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* At Risk */}
        <Box sx={{ flex: '1 1 calc(25% - 18px)', minWidth: 200 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  At Risk
                </Typography>
                <Tooltip
                  title="Accounts currently in the At Risk lifecycle stage (same as the stage distribution)"
                  arrow
                  placement="top"
                >
                  <IconButton size="small" sx={{ p: 0, mb: 0.5 }}>
                    <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="h3" fontWeight={600} color="warning.main">
                {metrics?.atRiskAccounts || 0}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Churned */}
        <Box sx={{ flex: '1 1 calc(25% - 18px)', minWidth: 200 }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Churned
              </Typography>
              <Typography variant="h3" fontWeight={600} color="error.main">
                {metrics?.churnedAccounts || 0}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Secondary Metrics */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {/* Average Health Score */}
        <Box sx={{ flex: '1 1 calc(50% - 12px)', minWidth: 300 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Average Health Score
              </Typography>
              <Typography variant="h2" fontWeight={600} color="text.primary">
                {metrics?.averageHealthScore?.toFixed(1) || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Out of 100
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Stage Distribution */}
        <Box sx={{ flex: '1 1 calc(50% - 12px)', minWidth: 300 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Stage Distribution
              </Typography>
              {metrics?.stageDistribution && Object.keys(metrics.stageDistribution).length > 0 ? (
                <Box sx={{ mt: 2 }}>
                  {Object.entries(metrics.stageDistribution).map(([stage, count]) => (
                    <Box
                      key={stage}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 1,
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: getStageHexColor(stage, config?.stageConfig),
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body2" textTransform="capitalize">
                          {stage.replace('_', ' ')}
                        </Typography>
                      </Box>
                      <Typography variant="body2" fontWeight={600}>
                        {count}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};
